import { FieldSet } from "client/lib/apt-merchant";

import { Field } from "../../field";
import { Merchant } from "../../merchant";
import { CountryList, ECommercePlatform, MerchantField } from "../../types";

type CountryListPlatformMap = {
  [key in CountryList]: ECommercePlatform[];
};
/**
 * AU / NZ are the same. Consider extracting? Premature optimization?
 * What happens if they deviate? Obv we could spread common, but is
 * this desirable?
 */
export const countryListPlatformMap: CountryListPlatformMap = {
  Australia: [
    "3dCart",
    "ACI Worldwide",
    "Adyen",
    "BigCommerce",
    "Black Pepper",
    "Bookwell",
    "ChargeAfter",
    "Citcon",
    "Commerce Vision",
    "CS-Cart",
    "Cybersource",
    "Demandware",
    "eCorner",
    "Ecwid",
    "Enflexion",
    "eStar",
    "Global-e",
    "Havealook",
    "Hybris",
    "Jetpack",
    "Kook",
    "Kovena",
    "Kynect",
    "Lightspeed",
    "Magento 1",
    "Magento 2",
    "MantisShop",
    "Marketplacer",
    "Maropost Commerce Cloud via Stripe",
    "Maropost Commerce Cloud (Neto)",
    "Mastercard",
    "Niche Fashion",
    "Nimbo",
    "Oncord",
    "OpenCart",
    "Other",
    "PPRO",
    "PrestaShop",
    "Pronto Avenue",
    "Rocketspark",
    "Salesforce Commerce Cloud",
    "SalonPay",
    "Shopify Plus",
    "Shopify",
    "Shoplazza",
    "Solutionists",
    "Spiffy Stores",
    "Square",
    "Squarespace",
    "Storbie",
    "StoreConnect",
    "StraightSell",
    "Stripe",
    "Web Ninja",
    "Website.World",
    "Wix",
    "WooCommerce",
    "Workarea",
    "Wpay",
    "Xcart",
    "ZEST (Zeald)",
  ],
  Canada: [
    "ACI Worldwide",
    "Adyen",
    "BigCommerce",
    "ChargeAfter",
    "Citcon",
    "Custom",
    "Cybersource",
    "Ecwid",
    "Hybris",
    "JP Morgan Chase",
    "Magento 1",
    "Magento 2",
    "Mastercard",
    "Nimbo",
    "OpenCart",
    "Other",
    "PPRO",
    "PrestaShop",
    "Salesforce Commerce Cloud",
    "SensePass",
    "Shopify",
    "Shoplazza",
    "Square",
    "Squarespace",
    "StoreConnect",
    "Stripe",
    "Wix",
    "WooCommerce",
  ],
  "New Zealand": [
    "3dCart",
    "ACI Worldwide",
    "Adyen",
    "BigCommerce",
    "Black Pepper",
    "Bookwell",
    "ChargeAfter",
    "Citcon",
    "Commerce Vision",
    "CS-Cart",
    "Cybersource",
    "Demandware",
    "eCorner",
    "Ecwid",
    "Enflexion",
    "eStar",
    "Global-e",
    "Havealook",
    "Hybris",
    "Jetpack",
    "Kook",
    "Kovena",
    "Kynect",
    "Lightspeed",
    "Magento 1",
    "Magento 2",
    "MantisShop",
    "Marketplacer",
    "Maropost Commerce Cloud via Stripe",
    "Maropost Commerce Cloud (Neto)",
    "Mastercard",
    "Niche Fashion",
    "Nimbo",
    "Oncord",
    "OpenCart",
    "Other",
    "PPRO",
    "PrestaShop",
    "Pronto Avenue",
    "Rocketspark",
    "Salesforce Commerce Cloud",
    "SalonPay",
    "Shopify Plus",
    "Shopify",
    "Shoplazza",
    "Solutionists",
    "Spiffy Stores",
    "Square",
    "Squarespace",
    "Storbie",
    "StoreConnect",
    "StraightSell",
    "Stripe",
    "Web Ninja",
    "Website.World",
    "Wix",
    "WooCommerce",
    "Workarea",
    "Wpay",
    "Xcart",
    "ZEST (Zeald)",
  ],
  "United Kingdom": [
    "ACI Worldwide",
    "Adyen",
    "BigCommerce",
    "ChargeAfter",
    "Citcon",
    "Cybersource",
    "Ecwid",
    "Magento 1",
    "Magento 2",
    "Mastercard",
    "N/A",
    "Nimbo",
    "OpenCart",
    "Other",
    "PPRO",
    "PrestaShop",
    "Rocketspark",
    "Salesforce Commerce Cloud",
    "Shopify",
    "Shoplazza",
    "Square",
    "StoreConnect",
    "Stripe",
    "Wix",
    "WooCommerce",
  ],
  "United States": [
    "ACI Worldwide",
    "Adyen",
    "BigCommerce",
    "Bolt",
    "ChargeAfter",
    "Citcon",
    "Custom",
    "Cybersource",
    "Ecwid",
    "Hybris",
    "JP Morgan Chase",
    "Magento 1",
    "Magento 2",
    "Maropost Commerce Cloud via Stripe",
    "Maropost Commerce Cloud (Neto)",
    "Mastercard",
    "Nimbo",
    "OpenCart",
    "Other",
    "PPRO",
    "PrestaShop",
    "Rocketspark",
    "Salesforce Commerce Cloud",
    "SensePass",
    "Shopify",
    "Shoplazza",
    "Square",
    "Squarespace",
    "StoreConnect",
    "Stripe",
    "Website.World",
    "Wix",
    "WooCommerce",
  ],
};

export class PlatformField extends Field<ECommercePlatform> {
  constructor(
    model: Merchant,
    fieldSet: FieldSet<any>,
    opts: Partial<MerchantField<ECommercePlatform>> = {}
  ) {
    super(
      model,
      {
        dataType: "string",
        propertyName: "platform",
        uiFieldType: "Select",
        testValue: "Shopify",
        ...opts,
      },
      fieldSet
    );
  }

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    const salesChannel = this._model.storeDetails.salesChannel.value;

    // base case - not applicable without a registeredCountry
    if (!registeredCountry) {
      return false;
    }

    if (
      registeredCountry === "Canada" ||
      registeredCountry === "United Kingdom"
    ) {
      return true;
    } else if (
      registeredCountry === "Australia" ||
      registeredCountry === "New Zealand" ||
      registeredCountry === "United States"
    ) {
      if (
        salesChannel === "Online Only" ||
        salesChannel === "Online and In-store"
      ) {
        return true;
      }
    }
    return false;
  }

  get required(): boolean {
    return this.applicable;
  }

  get shown(): boolean {
    return this.applicable;
  }

  get pickList(): ECommercePlatform[] {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    return registeredCountry ? countryListPlatformMap[registeredCountry] : [];
  }
}
